import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useTheme } from "../context/ThemeContext";
import { Search, User, LogOut, Shield, Menu, X, ChevronDown, Settings, Sun, Moon, Wallet } from "lucide-react";


const NAV = [
  { to: "/", label: "Home", id: "home" },
  { to: "/news", label: "News", id: "news" },
  { to: "/categories", label: "Categories", id: "categories" },
  { to: "/community", label: "Community", id: "community" },
];

const MARKETS_LINKS = [
  { to: "/coins", label: "Live Prices", desc: "100 coins with real-time data" },
  { to: "/market", label: "Market Overview", desc: "BTC dominance, fear & greed" },
];

function MediaCryptoLogo() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" fill="#facc15"/>
      <polyline points="4,22 10,12 16,18 22,8 28,14" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <circle cx="28" cy="14" r="2" fill="black"/>
    </svg>
  );
}

function MarketsDropdown() {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        className="flex items-center gap-1 text-sm font-medium tracking-wide uppercase transition-colors text-zinc-300 hover:text-white"
      >
        Markets <ChevronDown className={"w-3 h-3 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="absolute top-full left-0 pt-2 w-56 z-50">
        <div className="bg-zinc-950 border border-white/10">
          {MARKETS_LINKS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="block px-4 py-3 hover:bg-white/5 transition group"
              onClick={() => setOpen(false)}
            >
              <div className="text-sm font-medium text-white group-hover:text-yellow-400 transition">{item.label}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{item.desc}</div>
            </Link>
          ))}
        </div>
        </div>
      )}
    </div>
  );
}

function SettingsDropdown() {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-9 h-9 border border-white/10 flex items-center justify-center text-zinc-400 hover:border-yellow-400 hover:text-yellow-400 transition"
      >
        <Settings className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-zinc-950 border border-white/10 z-50 p-4">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Settings</div>
          <p className="text-xs text-zinc-500 font-mono">Theme & currency settings coming soon.</p>
        </div>
      )}
    </div>
  );
}

function MobileThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      className="w-full flex items-center justify-between py-2"
    >
      <span className="text-base font-display font-bold uppercase tracking-wide text-white flex items-center gap-3">
        {theme === "dark" ? <Moon className="w-5 h-5 text-yellow-400" /> : <Sun className="w-5 h-5 text-yellow-400" />}
        {theme === "dark" ? "Dark Mode" : "Light Mode"}
      </span>
      <div className={"w-10 h-5 rounded-full transition-colors relative " + (theme === "light" ? "bg-yellow-400" : "bg-zinc-700")}>
        <div className={"absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform " + (theme === "light" ? "translate-x-5" : "translate-x-0.5")} />
      </div>
    </button>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = React.useState("");
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate("/news?q=" + encodeURIComponent(q.trim()));
      setMobileOpen(false);
    }
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 backdrop-blur-xl bg-black/70"
        data-testid="main-navbar"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-10 h-16 flex items-center justify-between gap-4">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="nav-logo" onClick={closeMobile}>
            <MediaCryptoLogo />
            <span className="font-display font-bold text-lg md:text-xl tracking-tight text-white">
              Media<span className="text-yellow-400">Crypto</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6">
            {NAV.map((n) => (
              <NavLink
                key={n.id}
                to={n.to}
                end={n.to === "/"}
                data-testid={"nav-" + n.id + "-link"}
                className={({ isActive }) =>
                  "text-sm font-medium tracking-wide uppercase transition-colors " +
                  (isActive ? "text-yellow-400" : "text-zinc-300 hover:text-white")
                }
              >
                {n.label}
              </NavLink>
            ))}
            <MarketsDropdown />
          </nav>

          {/* Desktop Right */}
          <div className="hidden lg:flex items-center gap-3">
            <SettingsDropdown />
            <form onSubmit={submitSearch} className="flex items-center bg-zinc-900/70 border border-white/10 px-3 h-9 focus-within:border-yellow-400">
              <Search className="w-4 h-4 text-zinc-500 mr-2 flex-shrink-0" />
              <input
                data-testid="nav-search-input"
                type="text"
                placeholder="Search..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="bg-transparent outline-none text-sm w-32 placeholder:text-zinc-500 text-white"
              />
            </form>

            {user ? (
              <div className="flex items-center gap-2">
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="nav-admin-link"
                    className="flex items-center gap-1 text-xs font-mono uppercase tracking-wide text-yellow-400 border border-yellow-400/30 px-3 py-2 hover:bg-yellow-400/10 transition"
                  >
                    <Shield className="w-3 h-3" /> Admin
                  </Link>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="nav-user-menu-trigger"
                      className="w-9 h-9 bg-yellow-400 text-black font-bold flex items-center justify-center hover:bg-yellow-500 transition"
                    >
                      {(user.name || user.email)[0].toUpperCase()}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-zinc-950 border-white/10 text-white rounded-none w-56 z-[60]">
                    <DropdownMenuLabel className="font-mono text-xs text-zinc-500">
                      {user.email}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="nav-profile-link" className="cursor-pointer">
                      <User className="w-4 h-4 mr-2" /> Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/portfolio")} className="cursor-pointer">
                    <Wallet className="w-4 h-4 mr-2" /> Portfolio
                      </DropdownMenuItem>
                    {user.role === "admin" && (
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin-link-dd" className="cursor-pointer text-yellow-400">
                        <Shield className="w-4 h-4 mr-2" /> Admin Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem onClick={logout} data-testid="nav-logout-button" className="cursor-pointer text-red-400">
                      <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" data-testid="nav-login-link">
                  <Button variant="ghost" className="text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">
                    Sign in
                  </Button>
                </Link>
                <Link to="/register" data-testid="nav-register-link">
                  <Button className="bg-yellow-400 text-black hover:bg-yellow-500 rounded-none font-bold uppercase tracking-wide">
                    Join
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Right */}
          <div className="flex lg:hidden items-center gap-2">
            {user ? (
              <button
                className="w-9 h-9 bg-yellow-400 text-black font-bold flex items-center justify-center hover:bg-yellow-500 transition"
                onClick={() => { navigate("/profile"); closeMobile(); }}
              >
                {(user.name || user.email)[0].toUpperCase()}
              </button>
            ) : (
              <Link to="/register" onClick={closeMobile}>
                <Button className="bg-yellow-400 text-black hover:bg-yellow-500 rounded-none font-bold uppercase tracking-wide text-xs px-3 h-8">
                  Join
                </Button>
              </Link>
            )}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-9 h-9 border border-white/20 flex items-center justify-center text-white hover:border-yellow-400 hover:text-yellow-400 transition"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

        </div>
      </header>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black pt-16 flex flex-col lg:hidden overflow-y-auto">
          <div className="flex flex-col px-6 py-6 space-y-1">

            {/* Nav Links */}
            {NAV.map((n) => (
              <NavLink
                key={n.id}
                to={n.to}
                end={n.to === "/"}
                onClick={closeMobile}
                className={({ isActive }) =>
                  "block py-4 text-lg font-display font-bold uppercase tracking-wide border-b border-white/10 transition " +
                  (isActive ? "text-yellow-400" : "text-white hover:text-yellow-400")
                }
              >
                {n.label}
              </NavLink>
            ))}

            {/* Mobile Markets Section */}
            <div className="border-b border-white/10">
              <div className="py-4 text-xs font-mono uppercase tracking-widest text-zinc-500">Markets</div>
              {MARKETS_LINKS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeMobile}
                  className="block py-3 pl-4 text-base font-display font-bold uppercase tracking-wide text-white hover:text-yellow-400 transition"
                >
                  {item.label}
                  <span className="block text-xs font-mono font-normal text-zinc-500 normal-case tracking-normal mt-0.5">{item.desc}</span>
                </Link>
              ))}
            </div>

            {user?.role === "admin" && (
              <Link
                to="/admin"
                onClick={closeMobile}
                className="block py-4 text-lg font-display font-bold uppercase tracking-wide border-b border-white/10 text-yellow-400"
              >
                Admin
              </Link>
            )}

            {/* Mobile Search */}
            <form onSubmit={submitSearch} className="flex items-center bg-zinc-900 border border-white/20 px-3 h-11 mt-4 focus-within:border-yellow-400">
              <Search className="w-4 h-4 text-zinc-500 mr-2 flex-shrink-0" />
              <input
                type="text"
                placeholder="Search articles..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="bg-transparent outline-none text-sm flex-1 placeholder:text-zinc-500 text-white"
              />
            </form>

            {/* Mobile Theme Toggle */}
            <div className="border-b border-white/10 py-4">
              <MobileThemeToggle />
            </div>

            {/* Mobile Auth */}
            <div className="pt-6 space-y-3">
              {user ? (
                <>
                  <Link
                    to="/profile"
                    onClick={closeMobile}
                    className="flex items-center gap-3 py-3 text-zinc-300 hover:text-white transition border-b border-white/10"
                  >
                    <User className="w-5 h-5" /> Profile
                  </Link>
                  <button
                    onClick={() => { logout(); closeMobile(); }}
                    className="flex items-center gap-3 py-3 text-red-400 hover:text-red-300 transition w-full"
                  >
                    <LogOut className="w-5 h-5" /> Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={closeMobile}
                    className="block w-full text-center border border-white/20 py-3 text-white font-bold uppercase tracking-wide hover:border-yellow-400 hover:text-yellow-400 transition"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    onClick={closeMobile}
                    className="block w-full text-center bg-yellow-400 text-black py-3 font-bold uppercase tracking-wide hover:bg-yellow-500 transition"
                  >
                    Join Free
                  </Link>
                </>
              )}
            </div>

          </div>
        </div>
      )}
    </>
  );
}